import {
  API_BASE_URL,
  PUBLIC_API_BASE_URL,
  fetchJsonWithRetry,
  getAuthHeaders,
} from "../API/Api";
import { getPaginationInfo } from "../Brands/brandUtils";

export const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

export const generateProductSlug = (name, sku = "") => {
  const base = slugify(name) || "product";
  if (String(sku || "").trim()) {
    return `${base}-${slugify(sku)}`;
  }
  return `${base}-${Date.now()}`;
};

export const getProductApiErrorMessage = (errorData, fallback) => {
  if (errorData?.message) return errorData.message;
  const errors = errorData?.errors;
  if (errors && typeof errors === "object") {
    const firstKey = Object.keys(errors)[0];
    if (firstKey && Array.isArray(errors[firstKey])) {
      return errors[firstKey][0];
    }
  }
  return fallback;
};

export const buildProductPayload = (formData) => ({
  name: formData.name.trim(),
  slug: formData.slug || generateProductSlug(formData.name, formData.sku),
  sku: formData.sku.trim() || null,
  code: formData.code.trim() || null,
  ingredients: formData.ingredients.trim() || null,
  price:
    formData.price === "" || formData.price == null ? null : formData.price,
  case_size: formData.case_size || null,
  layer: formData.layer ? Number(formData.layer) : null,
  pallet: formData.pallet ? Number(formData.pallet) : null,
  unit_barcode: formData.unit_barcode || null,
  case_barcode: formData.case_barcode || null,
  net_weight: formData.net_weight || null,
  vat: formData.vat || null,
  case_length: formData.case_length || null,
  case_width: formData.case_width || null,
  case_height: formData.case_height || null,
  gross_weight: formData.gross_weight || null,
  units_per_case: formData.units_per_case
    ? Number(formData.units_per_case)
    : null,
  tariff: formData.tariff || null,
  origin: formData.origin || null,
  image: formData.image || null,
  brand_id: formData.brand_id ? Number(formData.brand_id) : null,
  category_id: formData.category_id ? Number(formData.category_id) : null,
  filter_ids: (formData.filter_ids || []).map((id) => Number(id)),
  is_active: formData.is_active,
});

export const fetchCategories = async (signal) => {
  const responseData = await fetchJsonWithRetry(
    `${API_BASE_URL}/categories`,
    { headers: getAuthHeaders(), signal },
  );
  if (Array.isArray(responseData?.data)) return responseData.data;
  if (Array.isArray(responseData)) return responseData;
  return [];
};

const stripHeavyProductRelations = (product) => {
  if (!product || typeof product !== "object") return product;
  const { related_products, ...rest } = product;
  return rest;
};

export const fetchProductById = async (id, signal) => {
  const responseData = await fetchJsonWithRetry(
    `${API_BASE_URL}/admin/products/${id}`,
    { headers: getAuthHeaders(), signal },
  );

  if (Array.isArray(responseData?.data)) {
    const match =
      responseData.data.find((item) => String(item.id) === String(id)) || null;
    return stripHeavyProductRelations(match);
  }

  if (responseData?.data && typeof responseData.data === "object") {
    return stripHeavyProductRelations(responseData.data);
  }

  if (responseData && typeof responseData === "object" && responseData.id) {
    return stripHeavyProductRelations(responseData);
  }

  return null;
};

const PRODUCTS_PER_PAGE = 100;
const PAGE_FETCH_DELAY_MS = 300;
const MAX_PRODUCT_PAGES = 100;
const MAX_RETRY_ATTEMPTS = 5;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getRetryDelayMs = (res, attempt) => {
  const retryAfter = res.headers.get("Retry-After");
  if (retryAfter) {
    const asSeconds = Number(retryAfter);
    if (Number.isFinite(asSeconds)) {
      return Math.min(Math.max(asSeconds * 1000, 500), 20000);
    }
    const asDate = Date.parse(retryAfter);
    if (Number.isFinite(asDate)) {
      return Math.min(Math.max(asDate - Date.now(), 500), 20000);
    }
  }
  return Math.min(1000 * 2 ** attempt, 15000);
};

export const fetchProductsPage = async ({
  page = 1,
  perPage = PRODUCTS_PER_PAGE,
  search = "",
  signal,
} = {}) => {
  const query = String(search || "").trim();
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  });

  if (query) {
    params.set("q", query);
  }

  const url = query
    ? `${PUBLIC_API_BASE_URL}/products?${params.toString()}`
    : `${API_BASE_URL}/admin/products?${params.toString()}`;

  for (let attempt = 0; attempt < MAX_RETRY_ATTEMPTS; attempt += 1) {
    const res = await fetch(url, {
      headers: query
        ? { Accept: "application/json" }
        : getAuthHeaders(),
      signal,
    });

    if (res.status === 429 && attempt < MAX_RETRY_ATTEMPTS - 1) {
      if (signal?.aborted) {
        throw new DOMException("Aborted", "AbortError");
      }
      await sleep(getRetryDelayMs(res, attempt));
      continue;
    }

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    const responseData = await res.json();
    return getPaginationInfo(responseData);
  }

  throw new Error("HTTP error! Status: 429");
};

let inFlightAllProducts = null;

export const fetchAllProducts = async () => {
  if (inFlightAllProducts) {
    return inFlightAllProducts;
  }

  inFlightAllProducts = (async () => {
    const allProducts = [];
    let page = 1;
    let lastPage = 1;

    do {
      const {
        items,
        currentPage,
        lastPage: totalPages,
      } = await fetchProductsPage({
        page,
        perPage: PRODUCTS_PER_PAGE,
      });

      allProducts.push(...items);
      lastPage = Math.min(Number(totalPages) || 1, MAX_PRODUCT_PAGES);

      if (!items.length || currentPage >= lastPage || page >= lastPage) {
        break;
      }

      page += 1;
      await sleep(PAGE_FETCH_DELAY_MS);
    } while (page <= lastPage);

    return allProducts;
  })().finally(() => {
    inFlightAllProducts = null;
  });

  return inFlightAllProducts;
};

export const normalizeProductRow = (item, index) => ({
  index: index + 1,
  id: item.id,
  name: item.name || "",
  slug: item.slug || "",
  code: item.code || item.product_code || "",
  unit_barcode:
    item.unit_barcode || item.unitBarcode || item.barcode || "",
  price: item.price || "",
  image_url:
    item.image_url ||
    item?.images?.[0]?.image_url ||
    item?.images?.[0]?.url ||
    "",
  brand_name: item?.brand?.name || "",
  category_name: item?.category?.name || "",
  is_active: !!item?.is_active,
});
